// DevicesPage.tsx — `/devices`: the user's phone lens profiles (ARCHITECTURE.md
// §14). A card grid, most recently updated first; each card can be locked
// (the server stops fusing new scans into it), renamed (Modal + Text), reset
// (fusion state cleared, label kept) or deleted — the last two behind
// ui.useConfirm, never a native dialog.
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { BsArrowClockwise, BsSave } from 'react-icons/bs';
import { ui } from 'common';
import { devicesApi } from '../api';
import type { DeviceProfile } from '../api';
import { DeviceCard } from '../components/DeviceCard';
import { errorMessage } from '../format';

const { Text } = ui;

export default function DevicesPage() {
  const [profiles, setProfiles] = useState<DeviceProfile[] | null>(null);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [renaming, setRenaming] = useState<DeviceProfile | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [confirm, confirmDialog] = ui.useConfirm();

  const load = useCallback(async () => {
    try {
      const list = await devicesApi.list();
      console.debug(`[openscan/devices] ${list.length} lens profiles`);
      setProfiles(list);
      setError('');
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => [...(profiles ?? [])].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')), [profiles]);

  // run performs one action on a profile, merging the returned profile (or
  // reloading when the server answered without a body).
  const run = async (p: DeviceProfile, what: string, fn: () => Promise<DeviceProfile | null>): Promise<boolean> => {
    setBusyKey(p.key);
    try {
      const updated = await fn();
      console.info(`[openscan/devices] ${what} fp=${p.fingerprint.slice(0, 12)}…`);
      if (updated) setProfiles((prev) => prev?.map((x) => (x.key === p.key ? updated : x)) ?? prev);
      else await load();
      return true;
    } catch (err) {
      setError(`${what}: ${errorMessage(err)}`);
      return false;
    } finally {
      setBusyKey('');
    }
  };

  const onToggleLock = (p: DeviceProfile) => void run(p, p.locked ? 'unlock' : 'lock', () => devicesApi.patch(p.key, { locked: !p.locked }));

  const onReset = async (p: DeviceProfile) => {
    const ok = await confirm({
      title: 'Reset Lens Profile',
      body: `Forget the measured lens of "${p.label}" (${p.n} fused scans)? The label is kept; the next scan from this phone calibrates from scratch.`,
      confirmLabel: 'Reset',
      variant: 'warning',
    });
    if (ok) await run(p, 'reset', () => devicesApi.reset(p.key));
  };

  const onDelete = async (p: DeviceProfile) => {
    const ok = await confirm({
      title: 'Delete Lens Profile',
      body: `Delete the lens profile "${p.label}"? Scans keep their own calibration; this cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    // A null result makes run reload the list, which drops the deleted card.
    await run(p, 'delete', async () => {
      await devicesApi.remove(p.key);
      return null;
    });
  };

  const saveRename = async () => {
    const p = renaming;
    const label = newLabel.trim();
    if (!p || !label) return;
    if (await run(p, 'rename', () => devicesApi.patch(p.key, { label }))) setRenaming(null);
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h1 className="h3 m-0 me-auto">Devices</h1>
        <Button variant="outline-secondary" onClick={() => void load()}>
          <BsArrowClockwise className="me-2" />
          Refresh
        </Button>
      </div>
      <p className="text-body-secondary prose-column">
        Every refined scan measures your phone&apos;s lens. Scans from the same phone, camera, resolution and zoom fuse into one profile, and
        the next capture starts from it instead of calibrating again. Lock a profile to stop it changing.
      </p>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {profiles === null && !error && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {profiles !== null && sorted.length === 0 && (
        <p className="text-body-secondary prose-column">No lens profiles yet. Capture a scan from your phone; the profile appears once it is created.</p>
      )}

      <div className="card-grid">
        {sorted.map((p) => (
          <DeviceCard
            key={p.key}
            profile={p}
            busy={busyKey === p.key}
            onToggleLock={onToggleLock}
            onReset={(x) => void onReset(x)}
            onDelete={(x) => void onDelete(x)}
            onRename={(x) => {
              setRenaming(x);
              setNewLabel(x.label);
            }}
          />
        ))}
      </div>

      <Modal show={!!renaming} onHide={() => setRenaming(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 m-0">Rename Device</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Text
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void saveRename()}
            style={{ width: '100%' }}
            aria-label="Device label"
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={() => setRenaming(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void saveRename()} disabled={!newLabel.trim() || !!busyKey}>
            <BsSave className="me-2" />
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      {confirmDialog}
    </>
  );
}
